import { useEffect, useId, useRef, useState } from "react";

interface SheetSelectDialogProps {
	filename: string;
	sheets: string[];
	alreadyLoaded?: string[];
	onSelect: (sheetNames: string[]) => void;
	onCancel: () => void;
}

/**
 * Модальный диалог выбора листов Excel.
 * Показывается когда файл содержит несколько видимых листов.
 * Пользователь выбирает один или несколько листов через чекбоксы или отменяет загрузку.
 *
 * Если передан `alreadyLoaded` — эти листы отображаются серыми с зелёной галочкой
 * и недоступны для выбора (они уже загружены ранее).
 */
export function SheetSelectDialog({
	filename,
	sheets,
	alreadyLoaded,
	onSelect,
	onCancel,
}: SheetSelectDialogProps) {
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const titleId = useId();
	const hintId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	const loadedSet = new Set(alreadyLoaded ?? []);
	const isReupload = loadedSet.size > 0;

	// Показать как modal при монтировании — focus-trap и Escape обеспечивает браузер
	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	// Escape handled by <dialog> — hook on cancel event
	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	// Объединённый список для отображения: сперва доступные, потом уже загруженные
	const displaySheets = [...sheets, ...Array.from(loadedSet).filter((s) => !sheets.includes(s))];

	const toggle = (sheet: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(sheet)) {
				next.delete(sheet);
			} else {
				next.add(sheet);
			}
			return next;
		});
	};

	const handleConfirm = () => {
		if (selected.size > 0) {
			onSelect(Array.from(selected));
		}
	};

	const title = isReupload ? "Выберите дополнительные листы" : "Выберите листы для загрузки";

	return (
		<dialog
			ref={dialogRef}
			className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
			aria-describedby={hintId}
		>
			<h3 id={titleId} className="mb-1 text-lg font-semibold text-slate-900">
				{title}
			</h3>
			<p id={hintId} className="mb-4 text-sm text-slate-500 truncate" title={filename}>
				{filename}
			</p>

			<div className="mb-4 max-h-64 overflow-y-auto space-y-1">
				{displaySheets.map((sheet) => {
					const displayName = sheet.trim() === "" ? "(без названия)" : sheet;
					const isLoaded = loadedSet.has(sheet);
					const isChecked = selected.has(sheet);

					if (isLoaded) {
						return (
							<div
								key={sheet}
								className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-400 cursor-not-allowed"
							>
								<span
									aria-hidden="true"
									className="inline-flex h-4 w-4 items-center justify-center text-green-600"
								>
									{"\u2713"}
								</span>
								<span className="text-sm line-through">{displayName}</span>
								<span className="ml-auto rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
									Загружен
								</span>
							</div>
						);
					}

					return (
						<label
							key={sheet}
							className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
								isChecked ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
							}`}
						>
							<input
								type="checkbox"
								checked={isChecked}
								onChange={() => toggle(sheet)}
								className="h-4 w-4 text-blue-600"
							/>
							<span className="text-sm text-slate-900">{displayName}</span>
						</label>
					);
				})}
			</div>

			<div className="flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
				>
					Отмена
				</button>
				<button
					type="button"
					onClick={handleConfirm}
					disabled={selected.size === 0}
					className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
				>
					Выбрать
				</button>
			</div>
		</dialog>
	);
}
